import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Messages } from 'primereact/messages';
import { Card } from 'primereact/card';
import { Toolbar } from 'primereact/toolbar';
import Container from 'react-bootstrap/Container';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';
import keypadCodesApi from '../api/keypadCodesApi';

import '../config.js';

const CODE_SCOPE = {
    WHOLE_LOCATION: 'whole-location',
    SPECIFIC_KEYPADS: 'specific-keypads',
};

const getApiErrorMessage = (error, fallbackMessage) => {
    if (error && error.response && typeof error.response.data === 'string' && error.response.data.trim()) {
        return error.response.data;
    }

    if (error && error.response && error.response.data && typeof error.response.data === 'object') {
        if (error.response.data.message) return error.response.data.message;
        if (error.response.data.title) return error.response.data.title;
    }

    return (error && error.message) || fallbackMessage;
};

const normalizeAjaxKeypads = (items) => {
    if (!Array.isArray(items)) return [];

    return items
        .map((item) => {
            const rawId = item.id || item.ajaxKeyPadId || item.ajaxKeypadId || item.keyPadId || item.keypadId;
            const parsedId = Number(rawId);
            if (!Number.isFinite(parsedId)) return null;

            const name = item.name || item.keyPadName || item.label || `Keypad ${parsedId}`;

            return {
                label: String(name),
                value: parsedId,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));
};

const getKeypadCodeId = (keypadCode) => {
    const parsedId = Number(keypadCode && keypadCode.id);
    return Number.isFinite(parsedId) ? parsedId : null;
};

const KeypadCodes = () => {
    useStatusPageScript();

    const { locid } = useParams();
    const navigate = useNavigate();
    const msgs = useRef(null);
    const toast = useRef(null);

    const [keypadCodes, setKeypadCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedKeyPad, setSelectedKeyPad] = useState(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [bulkDeleteVisible, setBulkDeleteVisible] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [codeScope, setCodeScope] = useState('');
    const [expiryDateTime, setExpiryDateTime] = useState(null);
    const [locationKeypads, setLocationKeypads] = useState([]);
    const [selectedAjaxKeyPadIds, setSelectedAjaxKeyPadIds] = useState([]);
    const [selectedKeyPadCodeIds, setSelectedKeyPadCodeIds] = useState([]);
    const [keypadsLoading, setKeypadsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [enforceDialogVisible, setEnforceDialogVisible] = useState(false);
    const [enforceLoading, setEnforceLoading] = useState(false);
    const [staffCodeToEnforce, setStaffCodeToEnforce] = useState(null);
    const [enforceResult, setEnforceResult] = useState(null);
    const [enforceRetryVisible, setEnforceRetryVisible] = useState(false);
    const [enforceRetryMessage, setEnforceRetryMessage] = useState('');
    const [regenerateDialogVisible, setRegenerateDialogVisible] = useState(false);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [staffCodeToRegenerate, setStaffCodeToRegenerate] = useState(null);
    const [regenerateResult, setRegenerateResult] = useState(null);

    const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
        deniedTitle: 'Keypad Codes Access Denied',
        deniedMessage: 'You do not have permission to view keypad codes for this location.',
    });

    useEffect(() => {
        getKeyPadCodes();
    }, [locid]);

    useEffect(() => {
        const availableIds = keypadCodes
            .map((item) => getKeypadCodeId(item))
            .filter((itemId) => itemId !== null);

        setSelectedKeyPadCodeIds((prev) => prev.filter((itemId) => availableIds.indexOf(itemId) !== -1));
    }, [keypadCodes]);

    const authHeader = () => {
        const token = localStorage.getItem('token');
        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    };

    const getKeyPadCodes = async (preferredSelectionId) => {
        clearAccessIssue();
        setLoading(true);

        try {
            const response = await axios.get(`${global.config.url}/locations/keypadcode/${locid}`, authHeader());
            const items = Array.isArray(response.data) ? response.data : [];
            const currentSelectedId = preferredSelectionId !== undefined
                ? preferredSelectionId
                : selectedKeyPad && selectedKeyPad.id;

            setKeypadCodes(items);

            if (currentSelectedId === null) {
                setSelectedKeyPad(null);
            } else if (currentSelectedId !== undefined) {
                const matchedSelection = items.find((item) => item.id === currentSelectedId);
                setSelectedKeyPad(matchedSelection || null);
            }
        } catch (error) {
            if (handleAccessError(error)) {
                setLoading(false);
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to load keypad codes.');
            msgs.current.show({ severity: 'error', summary: '', detail, life: 5000 });
        } finally {
            setLoading(false);
        }
    };

    const getLocationKeypads = async () => {
        if (keypadsLoading) return;

        clearAccessIssue();
        setKeypadsLoading(true);

        try {
            const response = await axios.get(`${global.config.url}/locations/keypads/${locid}`, authHeader());
            setLocationKeypads(normalizeAjaxKeypads(response.data));
        } catch (error) {
            if (handleAccessError(error, { deniedMessage: 'You do not have permission to view location keypads.' })) {
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to load location keypads.');
            toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
        } finally {
            setKeypadsLoading(false);
        }
    };

    const resetCreateForm = () => {
        setCodeScope('');
        setExpiryDateTime(null);
        setSelectedAjaxKeyPadIds([]);
        setFormErrors({});
    };

    const openCreateDialog = () => {
        resetCreateForm();
        setCreateVisible(true);
    };

    const closeCreateDialog = () => {
        if (createLoading) return;
        setCreateVisible(false);
        resetCreateForm();
    };

    const handleScopeChange = async (nextScope) => {
        setCodeScope(nextScope);
        setFormErrors((prev) => ({ ...prev, scope: null, keypadSelection: null }));

        if (nextScope !== CODE_SCOPE.SPECIFIC_KEYPADS) {
            setSelectedAjaxKeyPadIds([]);
            return;
        }

        if (locationKeypads.length === 0) {
            await getLocationKeypads();
        }
    };

    const validateCreateForm = () => {
        const errors = {};

        if (!codeScope) {
            errors.scope = 'Code scope is required.';
        }

        if (codeScope === CODE_SCOPE.SPECIFIC_KEYPADS && selectedAjaxKeyPadIds.length === 0) {
            errors.keypadSelection = 'Select at least one keypad.';
        }

        if (!expiryDateTime) {
            errors.expiryDateTime = 'Expiry date/time is required.';
        } else if (expiryDateTime.getTime() <= Date.now()) {
            errors.expiryDateTime = 'Expiry date/time must be in the future.';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const createKeypadCode = async () => {
        if (!validateCreateForm()) {
            return;
        }

        setCreateLoading(true);
        clearAccessIssue();

        try {
            const timeDifferenceMs = expiryDateTime.getTime() - Date.now();
            const expiryHours = Math.max(1, Math.ceil(timeDifferenceMs / (1000 * 60 * 60)));

            if (codeScope === CODE_SCOPE.WHOLE_LOCATION) {
                await axios.post(
                    `${global.config.url}/locations/keypadcode/${locid}`,
                    { expiryHours },
                    authHeader()
                );
            } else {
                await axios.post(
                    `${global.config.url}/locations/keypadcode/${locid}/bulk`,
                    {
                        ajaxKeyPadIds: selectedAjaxKeyPadIds,
                        expiryHours,
                    },
                    authHeader()
                );
            }

            toast.current.show({ severity: 'success', summary: '', detail: 'Keypad code created successfully.', life: 4000 });
            setCreateVisible(false);
            resetCreateForm();
            await getKeyPadCodes();
        } catch (error) {
            if (handleAccessError(error, { deniedMessage: 'You do not have permission to create keypad codes for this location.' })) {
                setCreateLoading(false);
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to create keypad code.');
            toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
        } finally {
            setCreateLoading(false);
        }
    };

    const openDeleteDialog = () => {
        if (!selectedKeyPad || !selectedKeyPad.id) {
            return;
        }

        setDeleteVisible(true);
    };

    const closeDeleteDialog = () => {
        if (deleteLoading) return;
        setDeleteVisible(false);
    };

    const closeBulkDeleteDialog = () => {
        if (bulkDeleteLoading) return;
        setBulkDeleteVisible(false);
    };

    const openBulkDeleteDialog = () => {
        if (selectedKeyPadCodeIds.length === 0) {
            return;
        }

        setBulkDeleteVisible(true);
    };

    const deleteKeypadCode = async () => {
        if (!selectedKeyPad || !selectedKeyPad.id) {
            msgs.current.show({ severity: 'error', summary: '', detail: 'Please select a keypad code to delete.', life: 4000 });
            return;
        }

        clearAccessIssue();
        setDeleteLoading(true);

        try {
            const deletingId = selectedKeyPad.id;

            await axios.delete(
                `${global.config.url}/locations/keypadcode/${locid}/${deletingId}`,
                authHeader()
            );

            toast.current.show({ severity: 'success', summary: '', detail: 'Keypad code deleted successfully.', life: 4000 });
            setDeleteVisible(false);
            setSelectedKeyPad(null);
            await getKeyPadCodes(null);
        } catch (error) {
            if (handleAccessError(error, { deniedMessage: 'You do not have permission to delete keypad codes for this location.' })) {
                setDeleteLoading(false);
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to delete keypad code.');
            toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
        } finally {
            setDeleteLoading(false);
        }
    };

    const toggleKeyPadCodeSelection = (keypadCodeId, checked) => {
        if (!Number.isFinite(keypadCodeId)) {
            return;
        }

        setSelectedKeyPadCodeIds((prev) => {
            const alreadySelected = prev.indexOf(keypadCodeId) !== -1;

            if (checked && !alreadySelected) {
                return [...prev, keypadCodeId];
            }

            if (!checked && alreadySelected) {
                return prev.filter((itemId) => itemId !== keypadCodeId);
            }

            return prev;
        });
    };

    const displayedKeyPadCodeIds = keypadCodes
        .map((item) => getKeypadCodeId(item))
        .filter((itemId) => itemId !== null);
    const selectedDisplayedKeyPadCodeCount = displayedKeyPadCodeIds.filter(
        (itemId) => selectedKeyPadCodeIds.indexOf(itemId) !== -1
    ).length;
    const allDisplayedKeyPadCodesSelected = displayedKeyPadCodeIds.length > 0
        && selectedDisplayedKeyPadCodeCount === displayedKeyPadCodeIds.length;
    const someDisplayedKeyPadCodesSelected = selectedDisplayedKeyPadCodeCount > 0
        && !allDisplayedKeyPadCodesSelected;

    const toggleSelectAllDisplayedKeyPadCodes = (checked) => {
        if (checked) {
            setSelectedKeyPadCodeIds(displayedKeyPadCodeIds);
            return;
        }

        setSelectedKeyPadCodeIds([]);
    };

    const deleteSelectedKeypadCodes = async () => {
        if (selectedKeyPadCodeIds.length === 0) {
            msgs.current.show({ severity: 'error', summary: '', detail: 'Please select at least one keypad code to delete.', life: 4000 });
            return;
        }

        clearAccessIssue();
        setBulkDeleteLoading(true);

        try {
            const currentSelectedKeyPadId = selectedKeyPad ? selectedKeyPad.id : undefined;
            const result = await keypadCodesApi.bulkDeleteKeypadCodes(locid, selectedKeyPadCodeIds);
            const deletedCount = countItems(result.deletedIds);
            const notFoundCount = countItems(result.notFoundIds);
            const failedCount = countItems(result.failedCodes);

            toast.current.show({
                severity: 'success',
                summary: '',
                detail: `${deletedCount} keypad code${deletedCount === 1 ? '' : 's'} deleted.`,
                life: 4000,
            });

            if (notFoundCount > 0) {
                toast.current.show({
                    severity: 'warn',
                    summary: '',
                    detail: `${notFoundCount} keypad code${notFoundCount === 1 ? '' : 's'} ${notFoundCount === 1 ? 'was' : 'were'} not found: ${result.notFoundIds.join(', ')}.`,
                    life: 7000,
                });
            }

            if (failedCount > 0) {
                toast.current.show({
                    severity: 'error',
                    summary: '',
                    detail: result.failedCodes
                        .map((item) => `Code ${item.ajaxKeyPadCodeId === null ? 'unknown' : item.ajaxKeyPadCodeId}: ${item.message}`)
                        .join(' | '),
                    life: 9000,
                });
            }

            setBulkDeleteVisible(false);
            setSelectedKeyPadCodeIds([]);
            await getKeyPadCodes(currentSelectedKeyPadId);
        } catch (error) {
            if (handleAccessError(error, { deniedMessage: 'You do not have permission to delete keypad codes for this location.' })) {
                setBulkDeleteLoading(false);
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to delete selected keypad codes.');
            toast.current.show({ severity: 'error', summary: '', detail, life: 5000 });
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    const countItems = (items) => (Array.isArray(items) ? items.length : 0);

    const openEnforceStaffDialog = (keypadCode) => {
        clearRetryState();
        setStaffCodeToEnforce(keypadCode);
        setEnforceDialogVisible(true);
    };

    const closeEnforceStaffDialog = () => {
        if (enforceLoading) return;
        setEnforceDialogVisible(false);
    };

    const clearRetryState = () => {
        setEnforceRetryVisible(false);
        setEnforceRetryMessage('');
    };

    const runEnforceStaffCode = async () => {
        clearAccessIssue();
        clearRetryState();
        setEnforceLoading(true);

        try {
            const result = await keypadCodesApi.enforceStaffCode(locid);
            const totalHubs = countItems(result.targetHubIds);
            const succeededHubs = countItems(result.succeededHubIds);
            const offlineHubs = countItems(result.offlineHubIds);
            const failedHubs = countItems(result.failedHubIds);

            setEnforceResult(result);
            setRegenerateResult(null);
            toast.current.show({
                severity: 'success',
                summary: 'Staff code sync complete',
                detail: `total hubs: ${totalHubs} | success: ${succeededHubs} | offline: ${offlineHubs} | failed: ${failedHubs}`,
                life: 8000,
            });

            setEnforceDialogVisible(false);
            await getKeyPadCodes(selectedKeyPad ? selectedKeyPad.id : undefined);
        } catch (error) {
            setEnforceDialogVisible(false);

            if (handleAccessError(error, { deniedMessage: 'You do not have permission to force sync staff codes for this location.' })) {
                setEnforceLoading(false);
                return;
            }

            const statusCode = error && error.response && error.response.status;
            if (statusCode === 404) {
                toast.current.show({ severity: 'error', summary: '', detail: 'Location or hubs not found', life: 5000 });
                setEnforceRetryVisible(false);
            } else {
                const message = 'Failed to sync staff code to hubs. Please retry.';
                toast.current.show({ severity: 'error', summary: '', detail: message, life: 6000 });
                setEnforceRetryVisible(true);
                setEnforceRetryMessage(message);
            }
        } finally {
            setEnforceLoading(false);
        }
    };

    const openRegenerateStaffDialog = (keypadCode) => {
        clearRetryState();
        setStaffCodeToRegenerate(keypadCode);
        setRegenerateDialogVisible(true);
    };

    const closeRegenerateStaffDialog = () => {
        if (regenerateLoading) return;
        setRegenerateDialogVisible(false);
    };

    const runRegenerateStaffCode = async () => {
        clearAccessIssue();
        clearRetryState();
        setRegenerateLoading(true);

        try {
            const result = await keypadCodesApi.regenerateStaffCode(locid);
            const totalHubs = countItems(result.targetHubIds);
            const succeededHubs = countItems(result.succeededHubIds);
            const offlineHubs = countItems(result.offlineHubIds);
            const failedHubs = countItems(result.failedHubIds);

            setRegenerateResult(result);
            setEnforceResult(null);
            toast.current.show({
                severity: 'success',
                summary: 'Staff code regenerated',
                detail: `New code: ${result.staffCode || '-'} | Policy: ${result.rotationPolicy || '-'} | Expiry (UTC): ${result.expiryUtc || '-'} | total hubs: ${totalHubs} | success: ${succeededHubs} | offline: ${offlineHubs} | failed: ${failedHubs}`,
                life: 9000,
            });

            setRegenerateDialogVisible(false);
            await getKeyPadCodes(selectedKeyPad ? selectedKeyPad.id : undefined);
        } catch (error) {
            setRegenerateDialogVisible(false);

            if (handleAccessError(error, { deniedMessage: 'You do not have permission to regenerate staff codes for this location.' })) {
                setRegenerateLoading(false);
                return;
            }

            const detail = getApiErrorMessage(error, 'Failed to regenerate staff code. Please try again.');
            toast.current.show({ severity: 'error', summary: '', detail, life: 6000 });
        } finally {
            setRegenerateLoading(false);
        }
    };

    const transformCreatedDate = (keypadCode) => {
        if (!keypadCode || !keypadCode.created) return '';
        return new Date(keypadCode.created).toLocaleString('en-GB');
    };

    const transformExpiryDate = (keypadCode) => {
        if (!keypadCode || !keypadCode.expiry) return '';
        return new Date(keypadCode.expiry).toLocaleString('en-GB', { timeZone: 'Europe/London' });
    };

    const formatBritishDateTimeFromUtc = (utcValue) => {
        if (!utcValue) return '-';

        const parsed = new Date(utcValue);
        if (Number.isNaN(parsed.getTime())) return '-';

        return parsed.toLocaleString('en-GB', {
            timeZone: 'Europe/London',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        });
    };

    const getSeverity = (option) => {
        switch (option) {
            case true:
                return 'success';
            case false:
                return 'warning';
            default:
                return null;
        }
    };

    const statusBodyTemplate = (keypadCode) => {
        let value = '';
        switch (keypadCode.isStaff) {
            case true:
                value = 'Staff';
                break;
            case false:
                value = 'Not Staff';
                break;
            default:
                value = 'Unknown';
                break;
        }

        return <Tag value={value} severity={getSeverity(keypadCode.isStaff)}></Tag>;
    };

    const enforceStaffBodyTemplate = (keypadCode) => {
        if (!keypadCode || keypadCode.isStaff !== true) {
            return null;
        }

        return (
            <div className="flex flex-wrap">
                <Button
                    label={regenerateLoading ? 'Regenerating staff code...' : 'Regenerate staff code'}
                    className="p-button-secondary p-button-sm mr-2 mb-1"
                    icon={regenerateLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                    disabled={loading || createLoading || deleteLoading || enforceLoading || regenerateLoading}
                    onClick={() => openRegenerateStaffDialog(keypadCode)}
                />
                <Button
                    label={enforceLoading ? 'Syncing staff code...' : 'Force sync staff code to hubs'}
                    className="p-button-warning p-button-sm mb-1"
                    icon={enforceLoading ? 'pi pi-spin pi-spinner' : 'pi pi-sync'}
                    disabled={loading || createLoading || deleteLoading || enforceLoading || regenerateLoading}
                    onClick={() => openEnforceStaffDialog(keypadCode)}
                />
            </div>
        );
    };

    const selectionHeaderTemplate = () => (
        <div onClick={(event) => event.stopPropagation()}>
            <Checkbox
                inputId="select-all-keypad-codes"
                checked={allDisplayedKeyPadCodesSelected}
                indeterminate={someDisplayedKeyPadCodesSelected}
                onChange={(e) => toggleSelectAllDisplayedKeyPadCodes(e.checked)}
                disabled={loading || createLoading || deleteLoading || bulkDeleteLoading || enforceLoading || regenerateLoading || displayedKeyPadCodeIds.length === 0}
            />
        </div>
    );

    const selectionBodyTemplate = (keypadCode) => {
        const keypadCodeId = getKeypadCodeId(keypadCode);

        return (
            <div onClick={(event) => event.stopPropagation()}>
                <Checkbox
                    checked={keypadCodeId !== null && selectedKeyPadCodeIds.indexOf(keypadCodeId) !== -1}
                    onChange={(e) => toggleKeyPadCodeSelection(keypadCodeId, e.checked)}
                    disabled={loading || createLoading || deleteLoading || bulkDeleteLoading || enforceLoading || regenerateLoading || keypadCodeId === null}
                />
            </div>
        );
    };

    const rowClassName = (keypadCode) => {
        if (selectedKeyPad && keypadCode && selectedKeyPad.id === keypadCode.id) {
            return 'p-highlight';
        }

        return '';
    };

    const busy = loading || createLoading || deleteLoading || bulkDeleteLoading || enforceLoading || regenerateLoading;

    const endContent = (
        <React.Fragment>
            <span className="p-input-icon-left mr-3 mt-1">
                <Button
                    icon="pi pi-refresh"
                    className="p-button-text mb-3 ml-2"
                    rounded
                    onClick={() => getKeyPadCodes()}
                    disabled={busy}
                    tooltip="Refresh KeyPad Codes"
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                    icon="pi pi-plus"
                    className="p-button-success mb-3 ml-2"
                    rounded
                    disabled={busy}
                    onClick={openCreateDialog}
                    tooltip="Create keypad code"
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                    label="Delete selected"
                    icon="pi pi-trash"
                    className="p-button-danger mb-3 ml-2"
                    loading={bulkDeleteLoading}
                    disabled={busy || selectedKeyPadCodeIds.length === 0}
                    onClick={openBulkDeleteDialog}
                />
                {/* <Button
                    icon="pi pi-trash"
                    className="p-button-danger mx-3 mb-3"
                    rounded
                    loading={deleteLoading}
                    disabled={busy || !selectedKeyPad}
                    onClick={openDeleteDialog}
                    tooltip="Delete selected keypad code"
                    tooltipOptions={{ position: 'bottom' }}
                /> */}
            </span>
        </React.Fragment>
    );

    const createDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={closeCreateDialog} className="p-button-text" disabled={createLoading} />
            <Button
                label="Create code"
                icon="pi pi-check"
                className="p-button-success"
                onClick={createKeypadCode}
                loading={createLoading}
                disabled={createLoading}
            />
        </div>
    );

    const deleteDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={closeDeleteDialog} className="p-button-text" disabled={deleteLoading} />
            <Button
                label="Delete"
                icon="pi pi-trash"
                className="p-button-danger"
                onClick={deleteKeypadCode}
                loading={deleteLoading}
                disabled={deleteLoading}
            />
        </div>
    );

    const bulkDeleteDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={closeBulkDeleteDialog} className="p-button-text" disabled={bulkDeleteLoading} />
            <Button
                label="Delete selected"
                icon="pi pi-trash"
                className="p-button-danger"
                onClick={deleteSelectedKeypadCodes}
                loading={bulkDeleteLoading}
                disabled={bulkDeleteLoading}
            />
        </div>
    );

    const enforceDialogFooter = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeEnforceStaffDialog}
                className="p-button-text"
                disabled={enforceLoading}
            />
            <Button
                label={enforceLoading ? 'Syncing staff code...' : 'Force sync staff code to hubs'}
                icon={enforceLoading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                className="p-button-warning"
                onClick={runEnforceStaffCode}
                disabled={enforceLoading}
            />
        </div>
    );

    const regenerateDialogFooter = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeRegenerateStaffDialog}
                className="p-button-text"
                disabled={regenerateLoading}
            />
            <Button
                label={regenerateLoading ? 'Regenerating staff code...' : 'Regenerate staff code'}
                icon={regenerateLoading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                className="p-button-secondary"
                onClick={runRegenerateStaffCode}
                disabled={regenerateLoading}
            />
        </div>
    );

    if (accessIssue) {
        return <AccessState {...accessIssue} />;
    }

    return (
        <Card className="justify-content-md-center mx-5">
            <Container>
                <div className="card">
                    <Toolbar
                        start={
                            <Container className="flex align-content-center flex-wrap">
                                <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex" />
                                <h4 className="flex mt-2">KeyPad Codes</h4>
                            </Container>
                        }
                        end={endContent}
                    />
                </div>

                <Messages ref={msgs} />
                <Toast ref={toast} position="top-center" />

                {enforceRetryVisible && (
                    <div className="surface-100 border-round p-3 mt-3 mb-2 flex align-items-center justify-content-between flex-wrap">
                        <span>{enforceRetryMessage}</span>
                        <Button
                            label="Retry"
                            className="p-button-secondary p-button-sm ml-3"
                            onClick={runEnforceStaffCode}
                            disabled={enforceLoading || regenerateLoading}
                        />
                    </div>
                )}

                {regenerateResult && (
                    <div className="surface-50 border-1 border-round p-3 mt-3 mb-2">
                        <div className="flex align-items-center justify-content-between mb-2">
                            <div className="font-bold">Staff code regeneration summary</div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-sm"
                                onClick={() => setRegenerateResult(null)}
                                tooltip="Close"
                                tooltipOptions={{ position: 'left' }}
                            />
                        </div>
                        <div>New code: {regenerateResult.staffCode || '-'}</div>
                        <div>Policy: {regenerateResult.rotationPolicy || '-'}</div>
                        <div>Expiry (British time): {formatBritishDateTimeFromUtc(regenerateResult.expiryUtc)}</div>
                        <div>total hubs: {countItems(regenerateResult.targetHubIds)}</div>
                        <div>success: {countItems(regenerateResult.succeededHubIds)}</div>
                        <div>offline: {countItems(regenerateResult.offlineHubIds)}</div>
                        <div>failed: {countItems(regenerateResult.failedHubIds)}</div>

                        {(countItems(regenerateResult.errors) > 0 || countItems(regenerateResult.failedHubIds) > 0) && (
                            <details className="mt-2">
                                <summary>
                                    Failed hub details ({countItems(regenerateResult.errors) > 0 ? countItems(regenerateResult.errors) : countItems(regenerateResult.failedHubIds)})
                                </summary>
                                <div className="mt-2">
                                    {countItems(regenerateResult.errors) > 0
                                        ? regenerateResult.errors.map((errorItem, idx) => (
                                            <div key={`regenerate-error-${idx}`}>
                                                Hub {errorItem.hubId === null ? 'unknown' : errorItem.hubId}: {errorItem.message}
                                            </div>
                                        ))
                                        : regenerateResult.failedHubIds.map((hubId) => (
                                            <div key={`regenerate-failed-hub-${hubId}`}>
                                                Hub {hubId}: Failed without additional error details.
                                            </div>
                                        ))}
                                </div>
                            </details>
                        )}
                    </div>
                )}

                {enforceResult && (
                    <div className="surface-50 border-1 border-round p-3 mt-3 mb-2">
                        <div className="flex align-items-center justify-content-between mb-2">
                            <div className="font-bold">Staff code sync summary</div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-sm"
                                onClick={() => setEnforceResult(null)}
                                tooltip="Close"
                                tooltipOptions={{ position: 'left' }}
                            />
                        </div>
                        <div>total hubs: {countItems(enforceResult.targetHubIds)}</div>
                        <div>success: {countItems(enforceResult.succeededHubIds)}</div>
                        <div>offline: {countItems(enforceResult.offlineHubIds)}</div>
                        <div>failed: {countItems(enforceResult.failedHubIds)}</div>

                        {countItems(enforceResult.errors) > 0 && (
                            <details className="mt-2">
                                <summary>Errors ({countItems(enforceResult.errors)})</summary>
                                <div className="mt-2">
                                    {enforceResult.errors.map((errorItem, idx) => (
                                        <div key={`enforce-error-${idx}`}>
                                            Hub {errorItem.hubId === null ? 'unknown' : errorItem.hubId}: {errorItem.message}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}
                    </div>
                )}

                <div className="justify-content-md-center mt-3">
                    <DataTable
                        value={keypadCodes}
                        loading={loading}
                        dataKey="id"
                        rowClassName={rowClassName}
                        onRowClick={(e) => setSelectedKeyPad(e.data)}
                    >
                        <Column header={selectionHeaderTemplate} body={selectionBodyTemplate} style={{ width: '3.5rem' }} />
                        <Column field="code" header="Code" sortable />
                        <Column field="created" header="Created at" body={transformCreatedDate} />
                        <Column field="expiry" header="Expires at" body={transformExpiryDate} sortable/>
                        <Column field="isStaff" header="Is Staff" body={statusBodyTemplate}></Column>
                        <Column header="Staff actions" body={enforceStaffBodyTemplate}></Column>
                        <Column field="index" header="Index" sortable></Column>
                    </DataTable>
                </div>

                <Dialog
                    visible={createVisible}
                    style={{ width: '34rem' }}
                    header="Create keypad code"
                    modal
                    className="p-fluid"
                    footer={createDialogFooter}
                    onHide={closeCreateDialog}
                >
                    <div className="p-field mb-3">
                        <label className="font-bold d-block mb-2">Code scope</label>
                        <div className="flex align-items-center mb-2">
                            <RadioButton
                                inputId="whole-location"
                                name="codeScope"
                                value={CODE_SCOPE.WHOLE_LOCATION}
                                onChange={(e) => handleScopeChange(e.value)}
                                checked={codeScope === CODE_SCOPE.WHOLE_LOCATION}
                                disabled={createLoading}
                            />
                            <label htmlFor="whole-location" className="ml-2 mb-0">Whole location</label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="specific-keypads"
                                name="codeScope"
                                value={CODE_SCOPE.SPECIFIC_KEYPADS}
                                onChange={(e) => handleScopeChange(e.value)}
                                checked={codeScope === CODE_SCOPE.SPECIFIC_KEYPADS}
                                disabled={createLoading}
                            />
                            <label htmlFor="specific-keypads" className="ml-2 mb-0">Specific keypad(s)</label>
                        </div>
                        {formErrors.scope && <small className="p-error d-block mt-2">{formErrors.scope}</small>}
                    </div>

                    {codeScope === CODE_SCOPE.SPECIFIC_KEYPADS && (
                        <div className="p-field mb-3">
                            <label htmlFor="ajax-keypads" className="font-bold d-block mb-2">Ajax keypads</label>
                            <MultiSelect
                                id="ajax-keypads"
                                value={selectedAjaxKeyPadIds}
                                options={locationKeypads}
                                optionLabel="label"
                                optionValue="value"
                                filter
                                display="chip"
                                placeholder={keypadsLoading ? 'Loading keypads...' : 'Select keypad(s)'}
                                disabled={createLoading || keypadsLoading}
                                onChange={(e) => {
                                    setSelectedAjaxKeyPadIds(e.value || []);
                                    setFormErrors((prev) => ({ ...prev, keypadSelection: null }));
                                }}
                                className={formErrors.keypadSelection ? 'p-invalid w-full' : 'w-full'}
                            />
                            {formErrors.keypadSelection && <small className="p-error d-block mt-2">{formErrors.keypadSelection}</small>}
                        </div>
                    )}

                    <div className="p-field">
                        <label htmlFor="expiry-datetime" className="font-bold d-block mb-2">Expiry date/time</label>
                        <Calendar
                            id="expiry-datetime"
                            value={expiryDateTime}
                            onChange={(e) => {
                                setExpiryDateTime(e.value || null);
                                setFormErrors((prev) => ({ ...prev, expiryDateTime: null }));
                            }}
                            showTime
                            hourFormat="24"
                            showIcon
                            dateFormat="dd/mm/yy"
                            minDate={new Date()}
                            disabled={createLoading}
                            className={formErrors.expiryDateTime ? 'p-invalid w-full' : 'w-full'}
                            placeholder="Select expiry date and time"
                        />
                        {formErrors.expiryDateTime && <small className="p-error d-block mt-2">{formErrors.expiryDateTime}</small>}
                    </div>
                </Dialog>

                <Dialog
                    visible={deleteVisible}
                    style={{ width: '26rem' }}
                    header="Confirm deletion"
                    modal
                    footer={deleteDialogFooter}
                    onHide={closeDeleteDialog}
                >
                    <p className="mb-0">Delete selected keypad code?</p>
                </Dialog>

                <Dialog
                    visible={bulkDeleteVisible}
                    style={{ width: '28rem' }}
                    header="Confirm bulk deletion"
                    modal
                    footer={bulkDeleteDialogFooter}
                    onHide={closeBulkDeleteDialog}
                >
                    <p className="mb-0">
                        Delete {selectedKeyPadCodeIds.length} selected keypad code{selectedKeyPadCodeIds.length === 1 ? '' : 's'}?
                    </p>
                </Dialog>

                <Dialog
                    visible={enforceDialogVisible}
                    style={{ width: '34rem' }}
                    header="Confirm staff code sync"
                    modal
                    footer={enforceDialogFooter}
                    onHide={closeEnforceStaffDialog}
                >
                    <p className="mb-0">
                        Force sync staff code to hubs{staffCodeToEnforce && staffCodeToEnforce.code ? ` (${staffCodeToEnforce.code})` : ''}?
                    </p>
                </Dialog>

                <Dialog
                    visible={regenerateDialogVisible}
                    style={{ width: '40rem' }}
                    header="Confirm staff code regeneration"
                    modal
                    footer={regenerateDialogFooter}
                    onHide={closeRegenerateStaffDialog}
                >
                    <p className="mb-0">
                        This will create a new staff code and push it to all online hubs in this location. Continue?
                    </p>
                    {staffCodeToRegenerate && staffCodeToRegenerate.code && (
                        <small className="d-block mt-2">Current staff code: {staffCodeToRegenerate.code}</small>
                    )}
                </Dialog>
            </Container>
        </Card>
    );
};

export default KeypadCodes;